/**
 * Mentor Dashboard Component
 *
 * Displays and manages clients for Elite Annual mentors.
 * Features:
 * - List of clients with status
 * - Add new client (with Stripe payment for client license)
 * - Remove client (with confirmation)
 * - Enter "masquerade mode" to view/edit client data
 */

import React, { useEffect, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import { useScope } from "../../hooks/useScope";
import { useTier } from "../../hooks/useTier";
import { useLanguage } from "../language/LanguageContext";
import { useHistory } from "react-router-dom";
import {
  checkSuperAdminStatus,
  createTestClientLicense,
  provisionClient,
  removeClient,
  updateClientInfo,
} from "../../services/mentorClientService";
import {
  createClientLicenseCheckout,
  createClientLicenseRenewal,
  formatLicenseExpiration,
  getClientLicensePrice,
  getClientLicensePricing,
  getLicenseStatusClass,
  getMentorClientsWithLicenseStatus,
} from "../../services/clientLicenseService";
import { useAnalytics } from "../../hooks/useAnalytics";
import Modal from "../common/Modal";
import "./MentorDashboard.css";

export const MentorDashboard = () => {
  const { currentUser } = useAuth();
  const { tierId } = useTier();
  const { translations } = useLanguage();
  const { enterManagedScope } = useScope();
  const history = useHistory();
  const analytics = useAnalytics();

  // Translation helper
  const t = (key, fallback) => {
    const keys = key.split(".");
    let value = translations;
    for (const k of keys) {
      value = value?.[k];
      if (value === undefined) return fallback;
    }
    return value || fallback;
  };

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newClientEmail, setNewClientEmail] = useState("");
  const [addingClient, setAddingClient] = useState(false);
  const [addError, setAddError] = useState(null);
  const [removeConfirm, setRemoveConfirm] = useState(null);
  const [editingClient, setEditingClient] = useState(null);
  const [editNickname, setEditNickname] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState(null);
  const [renewingClient, setRenewingClient] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [checkingSuperAdmin, setCheckingSuperAdmin] = useState(true);

  // Modal state for showing messages to users
  const [modalState, setModalState] = useState({
    isOpen: false,
    message: "",
    title: "",
    type: "info",
  });

  // Helper to show modal messages
  const showModal = (type, title, message) => {
    setModalState({
      isOpen: true,
      type,
      title,
      message,
    });
  };

  const closeModal = () => {
    setModalState((prev) => ({ ...prev, isOpen: false }));
  };

  // Get license pricing info
  const licensePricing = getClientLicensePricing();

  const loadClients = async () => {
    try {
      console.log("[MentorDashboard] Loading clients with license status...");
      setLoading(true);
      setError(null);
      // Use the new function that includes license status
      const clientsList = await getMentorClientsWithLicenseStatus();
      console.log(
        "[MentorDashboard] Clients loaded with license info:",
        clientsList
      );
      setClients(clientsList);
    } catch (err) {
      console.error("[MentorDashboard] Error loading clients:", err);
      console.error("[MentorDashboard] Error details:", {
        code: err.code,
        details: err.details,
        message: err.message,
      });
      setError(err.message || "Failed to load clients");
    } finally {
      setLoading(false);
    }
  };

  // Check if user is a super admin
  useEffect(() => {
    const checkAdmin = async () => {
      if (!currentUser) {
        setCheckingSuperAdmin(false);
        return;
      }

      try {
        const status = await checkSuperAdminStatus();
        setIsSuperAdmin(status.isSuperAdmin === true);
      } catch (error) {
        console.error("Error checking super admin status:", error);
      } finally {
        setCheckingSuperAdmin(false);
      }
    };

    checkAdmin();
  }, [currentUser]);

  // Fetch clients on mount
  useEffect(() => {
    const init = async () => {
      // Track mentor dashboard view
      try {
        analytics.trackScreenView({
          screen_class: "MentorDashboard",
          screen_name: "mentor_dashboard",
        });
      } catch (err) {
        console.error("Error tracking screen view:", err);
      }

      // Load clients
      await loadClients();
    };

    init();
  }, []); // Only run on mount

  const handleAddClient = async (e) => {
    e.preventDefault();
    setAddingClient(true);
    setAddError(null);

    try {
      // Validate email
      if (!newClientEmail || !newClientEmail.includes("@")) {
        throw new Error(
          t(
            "mentor.addClientModal.errors.invalidEmail",
            "Please enter a valid email address"
          )
        );
      }

      // If super admin, create test license instead of going through Stripe
      if (isSuperAdmin) {
        analytics.trackCTAClick({
          action: "super_admin_test_license_created",
          metadata: { client_email: newClientEmail },
          section: "mentor_dashboard",
          source: "add_client_modal",
        });

        const result = await createTestClientLicense(newClientEmail);
        showModal(
          "success",
          "Test License Created",
          `Test license created for ${newClientEmail}. The client has been provisioned with a 1-year test license.`
        );
        setShowAddModal(false);
        setNewClientEmail("");
        setAddingClient(false);
        await loadClients();
        return;
      }

      // Track client addition attempt
      analytics.trackCTAClick({
        action: "client_license_checkout_started",
        metadata: { client_email: newClientEmail },
        section: "mentor_dashboard",
        source: "add_client_modal",
      });

      // Create Stripe checkout session for client license ($109/year)
      // After successful payment, the webhook will provision the client with a 1-year license
      await createClientLicenseCheckout({
        userId: currentUser.uid,
        clientEmail: newClientEmail,
        // The success URL includes the client email so we can show success message
        // The webhook will handle calling provisionClient after successful payment
      });

      // The createClientLicenseCheckout function will redirect to Stripe
      // User will be redirected back to /mentor/clients?client_added=true after payment
      // Webhook will provision the client automatically with a 1-year license
    } catch (err) {
      console.error("Error initiating client license checkout:", err);
      setAddError(
        err.message ||
          t(
            "mentor.addClientModal.errors.checkoutFailed",
            "Failed to initiate checkout"
          )
      );
      setAddingClient(false);
    }
    // Note: Don't reset addingClient to false on success - the page will redirect
  };

  const handleRemoveClient = async (clientId) => {
    try {
      // Track client removal
      analytics.trackCTAClick({
        section: "mentor_dashboard",
        action: "client_removed",
        source: "remove_client_modal",
      });

      await removeClient(clientId);
      await loadClients();
      setRemoveConfirm(null);
    } catch (err) {
      console.error("Error removing client:", err);
      setRemoveConfirm(null);
      showModal(
        "error",
        t(
          "mentor.dashboard.errors.removeFailedTitle",
          "Failed to Remove Client"
        ),
        err.message ||
          t("mentor.dashboard.errors.unknownError", "An unknown error occurred")
      );
    }
  };

  const handleManageClient = (client) => {
    // Track masquerade mode entry
    analytics.trackCTAClick({
      section: "mentor_dashboard",
      action: "masquerade_enter",
      source: "manage_client_button",
    });

    // Enter masquerade mode
    // Use nickname if available, otherwise firstName/lastName, otherwise email
    const displayName =
      client.nickname ||
      `${client.firstName} ${client.lastName}`.trim() ||
      client.email;
    enterManagedScope(client.id, displayName);
    // Navigate to stored plans (history) as the default client view
    history.push("/history");
  };

  const handleEditClient = (client) => {
    setEditingClient(client);
    setEditNickname(client.nickname || "");
    setEditNotes(client.notes || "");
    setEditError(null);
  };

  const handleRenewLicense = async (client) => {
    setRenewingClient(client);

    try {
      // Track renewal attempt
      analytics.trackCTAClick({
        section: "mentor_dashboard",
        action: "license_renewal_started",
        source: "renew_license_button",
        metadata: { client_id: client.id },
      });

      // Create renewal checkout session
      await createClientLicenseRenewal({
        userId: currentUser.uid,
        clientEmail: client.email,
        clientUserId: client.id,
      });

      // The user will be redirected to Stripe Checkout
      // After successful payment, they'll be redirected back with client_renewed=true
    } catch (err) {
      console.error("Error initiating license renewal:", err);

      // Show user-friendly error message in modal
      let errorMessage =
        err.message ||
        t("mentor.dashboard.errors.unknownError", "An unknown error occurred");

      // Make error messages more user-friendly
      if (errorMessage.includes("No such customer")) {
        errorMessage = t(
          "mentor.dashboard.errors.noStripeCustomer",
          "Unable to process payment. Please contact support if this issue persists."
        );
      }

      showModal(
        "error",
        t(
          "mentor.dashboard.errors.renewalFailedTitle",
          "License Renewal Failed"
        ),
        errorMessage
      );

      setRenewingClient(null);
    }
  };

  const handleSaveClientEdit = async (e) => {
    e.preventDefault();
    if (!editingClient) return;

    setSavingEdit(true);
    setEditError(null);

    try {
      // Track client edit
      analytics.trackCTAClick({
        action: "client_edited",
        section: "mentor_dashboard",
        source: "edit_client_modal",
      });

      await updateClientInfo(editingClient.id, {
        nickname: editNickname,
        notes: editNotes,
      });

      // Reload clients to get updated data
      await loadClients();

      // Close modal
      setEditingClient(null);
      setEditNickname("");
      setEditNotes("");
    } catch (err) {
      console.error("Error updating client:", err);
      setEditError(err.message || "Failed to update client");
    } finally {
      setSavingEdit(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "active":
        return (
          <span className="status-badge status-active">
            {t("mentor.dashboard.status.active", "Active")}
          </span>
        );
      case "pending":
        return (
          <span className="status-badge status-pending">
            {t("mentor.dashboard.status.pending", "Pending")}
          </span>
        );
      case "rejected":
        return (
          <span className="status-badge status-rejected">
            {t("mentor.dashboard.status.rejected", "Rejected")}
          </span>
        );
      default:
        return (
          <span className="status-badge status-unknown">
            {t("mentor.dashboard.status.unknown", "Unknown")}
          </span>
        );
    }
  };

  const getLicenseBadge = (client) => {
    const { daysRemaining, licenseStatus } = client;
    const statusClass = getLicenseStatusClass(licenseStatus, daysRemaining);
    const expirationText = formatLicenseExpiration(daysRemaining);

    if (licenseStatus === "expired") {
      return (
        <span className={`license-badge ${statusClass}`} title={expirationText}>
          ⚠️ {t("mentor.dashboard.license.expired", "Expired")}
        </span>
      );
    }

    if (licenseStatus === "no_license") {
      return (
        <span className={`license-badge ${statusClass}`}>
          {t("mentor.dashboard.license.noLicense", "No License")}
        </span>
      );
    }

    if (daysRemaining <= 30) {
      return (
        <span className={`license-badge ${statusClass}`} title={expirationText}>
          ⚠️ {expirationText}
        </span>
      );
    }

    return (
      <span className={`license-badge ${statusClass}`} title={expirationText}>
        ✓ {expirationText}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="mentor-dashboard">
        <div className="loading">
          {t("mentor.dashboard.loading", "Loading clients...")}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mentor-dashboard">
        <div className="error-message">
          <h3>{t("mentor.dashboard.error", "Error")}</h3>
          <p>{error}</p>
          <button onClick={loadClients}>
            {t("mentor.dashboard.retry", "Retry")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mentor-dashboard">
      {/* Super Admin Banner */}
      {isSuperAdmin && (
        <div
          style={{
            backgroundColor: "#4caf50",
            color: "white",
            padding: "12px 16px",
            marginBottom: "20px",
            borderRadius: "4px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            fontWeight: "500",
          }}
        >
          <span>🛡️ Super Admin Mode - Payment checks bypassed for testing</span>
        </div>
      )}

      <div className="dashboard-header">
        <h1>{t("mentor.dashboard.title", "My Clients")}</h1>
        <button className="btn-primary" onClick={() => setShowAddModal(true)}>
          + {t("mentor.dashboard.addClient", "Add Client")}
        </button>
      </div>

      {clients.length === 0 ? (
        <div className="empty-state">
          <p>
            {t(
              "mentor.dashboard.emptyState",
              "You haven't added any clients yet."
            )}
          </p>
          <p>
            {t(
              "mentor.dashboard.emptyStateAction",
              'Click "Add Client" to get started.'
            )}
          </p>
        </div>
      ) : (
        <div className="clients-grid">
          {clients.map((client) => {
            // Get display name
            const displayName =
              client.nickname ||
              `${client.firstName || ""} ${client.lastName || ""}`.trim() ||
              client.email.split("@")[0];

            // Generate initials
            const getInitials = () => {
              if (client.firstName && client.lastName) {
                return `${client.firstName[0]}${client.lastName[0]}`.toUpperCase();
              }
              if (client.nickname) {
                return client.nickname.substring(0, 2).toUpperCase();
              }
              return client.email.substring(0, 2).toUpperCase();
            };

            // Format joined date
            const joinedDate =
              client.joinedAt && client.joinedAt._seconds
                ? new Date(client.joinedAt._seconds * 1000).toLocaleDateString()
                : "N/A";

            // Check if can manage
            const canManage =
              client.status === "active" &&
              (isSuperAdmin || client.licenseStatus === "active");

            // Check if needs renewal
            const needsRenewal =
              client.licenseStatus === "expired" ||
              client.licenseStatus === "no_license" ||
              (client.daysRemaining !== null && client.daysRemaining <= 30);

            return (
              <div key={client.id} className="client-card">
                {/* Card Header */}
                <div className="client-card-header">
                  {client.avatarPhoto ? (
                    <img
                      src={client.avatarPhoto}
                      alt={displayName}
                      className="client-avatar client-avatar-img"
                      onError={(e) => {
                        // Fallback to initials on error
                        e.target.style.display = "none";
                        e.target.nextSibling.style.display = "flex";
                      }}
                    />
                  ) : null}
                  <div
                    className="client-avatar"
                    style={{ display: client.avatarPhoto ? "none" : "flex" }}
                  >
                    {getInitials()}
                  </div>
                  <div className="client-info">
                    <h3 className="client-name">{displayName}</h3>
                    <span className="client-email">{client.email}</span>
                  </div>
                </div>

                {/* Status & License Row */}
                <div className="client-status-row">
                  {getStatusBadge(client.status)}
                  {getLicenseBadge(client)}
                </div>

                {/* Notes Section (if exists) */}
                {client.notes && (
                  <div className="client-notes">
                    <span className="notes-label">
                      📝 {t("mentor.dashboard.table.notes", "Notes")}
                    </span>
                    <p className="notes-text">{client.notes}</p>
                  </div>
                )}

                {/* Meta Info */}
                <div className="client-meta">
                  <span className="meta-item">
                    📅 {t("mentor.dashboard.table.joined", "Joined")}:{" "}
                    {joinedDate}
                  </span>
                </div>

                {/* Actions */}
                <div className="client-card-actions">
                  <button
                    className="client-action-btn action-manage"
                    onClick={() => handleManageClient(client)}
                    disabled={!canManage}
                    title={
                      !canManage
                        ? t(
                            "mentor.dashboard.actions.manageDisabled",
                            "Client must be active with valid license"
                          )
                        : ""
                    }
                  >
                    👤 {t("mentor.dashboard.actions.manage", "Manage")}
                  </button>

                  <button
                    className="client-action-btn action-edit"
                    onClick={() => handleEditClient(client)}
                    title={t("mentor.dashboard.actions.edit", "Edit")}
                  >
                    ✏️
                  </button>

                  {needsRenewal && (
                    <button
                      className="client-action-btn action-renew"
                      onClick={() => handleRenewLicense(client)}
                      disabled={renewingClient?.id === client.id}
                      title={t(
                        "mentor.dashboard.actions.renew",
                        "Renew License"
                      )}
                    >
                      {renewingClient?.id === client.id ? "..." : "🔄"}
                    </button>
                  )}

                  <button
                    className="client-action-btn action-remove"
                    onClick={() => setRemoveConfirm(client)}
                    title={t("mentor.dashboard.actions.remove", "Remove")}
                  >
                    🗑️
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add Client Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{t("mentor.addClientModal.title", "Add New Client")}</h2>
            <form onSubmit={handleAddClient}>
              <div className="form-group">
                <label htmlFor="clientEmail">
                  {t(
                    "mentor.addClientModal.emailLabel",
                    "Client Email Address"
                  )}
                </label>
                <input
                  type="email"
                  id="clientEmail"
                  value={newClientEmail}
                  onChange={(e) => setNewClientEmail(e.target.value)}
                  placeholder={t(
                    "mentor.addClientModal.emailPlaceholder",
                    "client@example.com"
                  )}
                  required
                  disabled={addingClient}
                />
              </div>

              {/* License pricing info */}
              {isSuperAdmin ? (
                <div
                  className="license-pricing-info"
                  style={{
                    backgroundColor: "rgba(76, 175, 80, 0.1)",
                    padding: "1rem",
                    borderRadius: "8px",
                    marginBottom: "1rem",
                    border: "1px solid rgba(76, 175, 80, 0.3)",
                  }}
                >
                  <p style={{ margin: 0, fontWeight: "500", color: "#333" }}>
                    🛡️ Super Admin Mode
                  </p>
                  <p style={{ margin: "0.5rem 0 0 0", fontSize: "0.95rem" }}>
                    A test license will be created without payment. This
                    privilege is for testing only.
                  </p>
                </div>
              ) : (
                <div
                  className="license-pricing-info"
                  style={{
                    backgroundColor: "rgba(212, 165, 116, 0.1)",
                    padding: "1rem",
                    borderRadius: "8px",
                    marginBottom: "1rem",
                    border: "1px solid rgba(212, 165, 116, 0.3)",
                  }}
                >
                  <p style={{ margin: 0, fontWeight: "500", color: "#333" }}>
                    💳{" "}
                    {t(
                      "mentor.addClientModal.pricingTitle",
                      "Client License Fee"
                    )}
                  </p>
                  <p style={{ margin: "0.5rem 0 0 0", fontSize: "0.95rem" }}>
                    {t(
                      "mentor.addClientModal.pricingDescription",
                      `$${licensePricing.amount} per client, per year. This license allows you and your client to work together using the platform.`
                    )}
                  </p>
                </div>
              )}

              {addError && <div className="error-message">{addError}</div>}
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowAddModal(false)}
                  disabled={addingClient}
                >
                  {t("mentor.addClientModal.cancel", "Cancel")}
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={addingClient}
                >
                  {addingClient
                    ? t("mentor.addClientModal.submitting", "Processing...")
                    : isSuperAdmin
                    ? t(
                        "mentor.addClientModal.submitTest",
                        "Create Test License & Add Client"
                      )
                    : t(
                        "mentor.addClientModal.submit",
                        `Pay $${licensePricing.amount} & Add Client`
                      )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Client Modal */}
      {editingClient && (
        <div className="modal-overlay" onClick={() => setEditingClient(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>
              {t("mentor.editClientModal.title", "Edit Client")}:{" "}
              {editingClient.email}
            </h2>
            <form onSubmit={handleSaveClientEdit}>
              <div className="form-group">
                <label htmlFor="editNickname">
                  {t("mentor.editClientModal.nickname", "Nickname")}
                </label>
                <input
                  type="text"
                  id="editNickname"
                  value={editNickname}
                  onChange={(e) => setEditNickname(e.target.value)}
                  placeholder={t(
                    "mentor.editClientModal.nicknamePlaceholder",
                    "Add a nickname for easier identification (optional)"
                  )}
                  disabled={savingEdit}
                />
                <small
                  style={{
                    display: "block",
                    marginTop: "0.5rem",
                    color: "rgba(0, 0, 0, 0.6)",
                    fontSize: "0.875rem",
                  }}
                >
                  {t(
                    "mentor.editClientModal.nicknameHelp",
                    "This nickname is only visible to you and helps you identify this client."
                  )}
                </small>
              </div>
              <div className="form-group">
                <label htmlFor="editNotes">
                  {t("mentor.editClientModal.notes", "Notes")}
                </label>
                <textarea
                  id="editNotes"
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  placeholder={t(
                    "mentor.editClientModal.notesPlaceholder",
                    "Add notes about this client..."
                  )}
                  rows={4}
                  disabled={savingEdit}
                  style={{
                    width: "100%",
                    padding: "1rem 1.25rem",
                    border: "2px solid rgba(0, 0, 0, 0.1)",
                    borderRadius: "12px",
                    fontSize: "1rem",
                    fontFamily: "'Inter', 'Segoe UI', Arial, sans-serif",
                    transition: "all 0.3s ease",
                    background: "white",
                    resize: "vertical",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "var(--accent, #d4a574)";
                    e.target.style.boxShadow =
                      "0 0 0 4px rgba(212, 165, 116, 0.1)";
                    e.target.style.background = "rgba(212, 165, 116, 0.02)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "rgba(0, 0, 0, 0.1)";
                    e.target.style.boxShadow = "none";
                    e.target.style.background = "white";
                  }}
                />
              </div>
              {editError && (
                <div className="error-message" style={{ marginBottom: "1rem" }}>
                  {editError}
                </div>
              )}
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setEditingClient(null)}
                  disabled={savingEdit}
                >
                  {t("mentor.editClientModal.cancel", "Cancel")}
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={savingEdit}
                >
                  {savingEdit
                    ? t("mentor.editClientModal.saving", "Saving...")
                    : t("mentor.editClientModal.save", "Save Changes")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Remove Confirmation Modal */}
      {removeConfirm && (
        <div className="modal-overlay" onClick={() => setRemoveConfirm(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{t("mentor.removeClientModal.title", "Remove Client")}</h2>
            <p>
              {t(
                "mentor.removeClientModal.message",
                "Are you sure you want to remove"
              )}{" "}
              <strong>{removeConfirm.email}</strong> from your client list?
            </p>
            <p className="warning-text">
              {t(
                "mentor.removeClientModal.warning",
                "This will revoke your access to their data. Their account and data will remain intact."
              )}
            </p>
            <div className="modal-actions">
              <button
                className="btn-secondary"
                onClick={() => setRemoveConfirm(null)}
              >
                {t("mentor.removeClientModal.cancel", "Cancel")}
              </button>
              <button
                className="btn-danger"
                onClick={() => handleRemoveClient(removeConfirm.id)}
              >
                {t("mentor.removeClientModal.confirm", "Remove Client")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* General Message Modal */}
      <Modal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        type={modalState.type}
        title={modalState.title}
        message={modalState.message}
        closeButtonText={t("common.close", "Close")}
      />
    </div>
  );
};

export default MentorDashboard;
